import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router';

/**
 * The router applies a navigation a moment after setSearchParams is called, so
 * a second update right behind the first (a tab click then a branch change, as
 * a fast user or a script does) would start from the old query string and drop
 * the first change. Remember the last write and build on it until the router
 * has caught up.
 */
const SAME_BURST_MS = 150;
let lastWrite: { from: string; to: string } | null = null;
let lastWriteTimer: ReturnType<typeof setTimeout> | undefined;

function updatedParams(
  current: URLSearchParams,
  change: (params: URLSearchParams) => void,
): URLSearchParams {
  const from = current.toString();
  const next = new URLSearchParams(
    lastWrite && lastWrite.from === from ? lastWrite.to : current,
  );
  change(next);
  lastWrite = { from, to: next.toString() };
  clearTimeout(lastWriteTimer);
  lastWriteTimer = setTimeout(() => (lastWrite = null), SAME_BURST_MS);
  return next;
}

interface UrlStateOptions {
  /** Use replace for rapidly changing inputs such as live-search text. */
  replace?: boolean;
}

/**
 * Keeps small, shareable UI state in the current route's query string.
 *
 * Tabs and drawers push a history entry so browser Back follows the in-app
 * interaction. Live filters can opt into replace while still surviving a
 * refresh. Default values are omitted to keep links readable.
 */
export function useUrlState<T extends string = string>(
  key: string,
  defaultValue: NoInfer<T>,
  allowed?: readonly T[],
  options: UrlStateOptions = {},
): [T, Dispatch<SetStateAction<T>>] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const value =
    raw !== null && (!allowed || allowed.includes(raw as T))
      ? (raw as T)
      : defaultValue;

  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (nextValue) => {
      const resolved =
        typeof nextValue === 'function' ? nextValue(value) : nextValue;
      setParams(
        (current) =>
          updatedParams(current, (next) => {
            if (resolved === defaultValue || resolved === '') next.delete(key);
            else next.set(key, resolved);
          }),
        { replace: options.replace ?? false },
      );
    },
    [defaultValue, key, options.replace, setParams, value],
  );

  return [value, setValue];
}

export function useOptionalUrlState(
  key: string,
  options: UrlStateOptions = {},
): [string | null, (value: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key);
  const setValue = useCallback(
    (nextValue: string | null) => {
      setParams(
        (current) =>
          updatedParams(current, (next) => {
            if (nextValue) next.set(key, nextValue);
            else next.delete(key);
          }),
        { replace: options.replace ?? false },
      );
    },
    [key, options.replace, setParams],
  );
  return [value, setValue];
}
